import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material"
import {
  DatePicker,
  DateTimePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers"
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment"
import { get } from "lodash-es"
import moment from "moment"
import { FC, useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { ReportDataset } from "shared/report-datasets"
import { useAppSelector } from "src/app/hooks"
import {
  getDateFormat,
  isValidReportDate,
  REPORT_MAX_DATE,
  REPORT_MIN_DATE,
  truncateDate,
} from "src/charts/chartsCommon"
import { DefaultColorMap } from "src/charts/shared"
import { selectTenantId } from "src/features/me/meSlice"
import { useRequestReportDataMutation } from "../features/report/reportApi"
import { Sl2Btn } from "./Sl2Btn"

export type OsReportReservationNewClientsProps = {}
export const OsReportReservationNewClients: FC<
  OsReportReservationNewClientsProps
> = () => {
  const { t } = useTranslation()

  const tenantId = useAppSelector(selectTenantId)

  const [requestReportData, { isLoading, isError }] =
    useRequestReportDataMutation()

  const [ds, setDs] = useState<ReportDataset<any> | null>(null)

  const [period, setPeriod] = useState("day")
  // last 1month
  const [startDate, setStartDate] = useState(
    moment(moment().subtract(1, "month")),
  )
  const [endDate, setEndDate] = useState(moment())

  const [snackMessage, setSnackMessage] = useState({
    message: "",
    severity: "success" as "success" | "error" | "warning" | "info" | undefined,
  })

  const handlePeriodChange = useCallback((event: SelectChangeEvent<string>) => {
    setPeriod(event.target.value as string)
  }, [])

  const handleStartDateChange = useCallback((date: moment.Moment | null) => {
    if (isValidReportDate(date)) setStartDate(date)
  }, [])

  const handleEndDateChange = useCallback((date: moment.Moment | null) => {
    if (isValidReportDate(date)) setEndDate(date)
  }, [])

  // validation error
  const [validationError, setValidationError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!tenantId) {
      return
    }
    try {
      setValidationError(null)
      if (startDate.isAfter(endDate)) {
        setSnackMessage({
          message: t("reporting.dateRangeError"),
          severity: "error",
        })
        setValidationError(t("reporting.dateRangeError"))
        return
      }
      const res = await requestReportData({
        body: {
          reportType: "ReservationNewClients",
          tenantId,
          input: {
            tenantId,
            period,
            startDate: truncateDate(startDate, period).format(
              getDateFormat(period),
            ),
            endDate: truncateDate(endDate, period).format(
              getDateFormat(period),
            ),
          },
        },
      }).unwrap()
      const datasets = get(res, "res.datasets", {}) as any
      if (datasets?.newClients) {
        setDs(datasets.newClients)
      }
    } catch (e) {
      console.error("Error fetching report data", e)
      setSnackMessage({
        message: t("reporting.fetchError"),
        severity: "error",
      })
    }
  }, [tenantId, requestReportData, period, startDate, endDate, t])

  useEffect(
    () => {
      refresh()
    } /* eslint-disable react-hooks/exhaustive-deps */,
    [tenantId],
  )

  const getMax = (data: any[]) => {
    return Math.max(...data.map((d) => d.new_clients))
  }

  return (
    <Stack direction="column" spacing={4} p={2}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack direction="column" spacing={0}>
          <Typography variant="body1" fontWeight={"bold"}>
            {t("reporting.reservationClientsBehavior.title")}
          </Typography>
          <Typography variant="body2">
            {t("reporting.reservationClientsBehavior.description")}
          </Typography>

          {/* filter controls */}
          <Stack direction="row" spacing={2} alignItems="center" pt={4}>
            <FormControl>
              <InputLabel id="period">{t("reporting.period")}</InputLabel>
              <Select
                id="period"
                value={period}
                onChange={handlePeriodChange}
                size="small"
                sx={{
                  minWidth: "200px",
                }}
                label={t("reporting.period")}
              >
                {/* <MenuItem value="hour">{t("reporting.period.hour")}</MenuItem> */}
                <MenuItem value="day">{t("reporting.period.day")}</MenuItem>
                <MenuItem value="week">{t("reporting.period.week")}</MenuItem>
                <MenuItem value="month">{t("reporting.period.month")}</MenuItem>
                <MenuItem value="year">{t("reporting.period.year")}</MenuItem>
              </Select>
            </FormControl>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <>
                {period === "hour" ? (
                  <DateTimePicker
                    views={["year", "month", "day", "hours"]}
                    label={t("reporting.startDate")}
                    // openTo="year"
                    value={startDate}
                    onChange={handleStartDateChange}
                    minDate={REPORT_MIN_DATE}
                    maxDate={REPORT_MAX_DATE}
                    slotProps={{ textField: { size: "small" } }}
                    format="YYYY/MM/DD hh A"
                  />
                ) : (
                  <DatePicker
                    views={
                      period === "year"
                        ? ["year"]
                        : period === "month"
                          ? ["year", "month"]
                          : ["year", "month", "day"]
                    }
                    label={t("reporting.startDate")}
                    // openTo="year"
                    value={startDate}
                    onChange={handleStartDateChange}
                    minDate={REPORT_MIN_DATE}
                    maxDate={REPORT_MAX_DATE}
                    slotProps={{ textField: { size: "small" } }}
                    format={
                      period === "year"
                        ? "YYYY"
                        : period === "month"
                          ? "YYYY/MM"
                          : "YYYY/MM/DD"
                    }
                  />
                )}
              </>
              <>
                {period === "hour" ? (
                  <DateTimePicker
                    views={["year", "month", "day", "hours"]}
                    label={t("reporting.endDate")}
                    // openTo="year"
                    value={endDate}
                    onChange={handleEndDateChange}
                    minDate={REPORT_MIN_DATE}
                    maxDate={REPORT_MAX_DATE}
                    slotProps={{ textField: { size: "small" } }}
                    format="YYYY/MM/DD hh A"
                  />
                ) : (
                  <DatePicker
                    views={
                      period === "year"
                        ? ["year"]
                        : period === "month"
                          ? ["year", "month"]
                          : ["year", "month", "day"]
                    }
                    label={t("reporting.endDate")}
                    // openTo="year"
                    value={endDate}
                    onChange={handleEndDateChange}
                    minDate={REPORT_MIN_DATE}
                    maxDate={REPORT_MAX_DATE}
                    slotProps={{ textField: { size: "small" } }}
                    format={
                      period === "year"
                        ? "YYYY"
                        : period === "month"
                          ? "YYYY/MM"
                          : "YYYY/MM/DD"
                    }
                  />
                )}
              </>
            </LocalizationProvider>

            <Sl2Btn
              onClick={refresh}
              disabled={isLoading}
              sx={{
                minWidth: "100px",
              }}
              variant="contained"
              color="primary"
            >
              {t("apply")}
            </Sl2Btn>
            <Typography variant="caption">
              {ds?.lastUpdated &&
                `${t("lastUpdated")}: ${moment(ds.lastUpdated).format(
                  "YYYY-MM-DD HH:mm:ss",
                )}`}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      {validationError && <Alert severity="error">{validationError}</Alert>}

      <Box>
        {isError && <Alert severity="error">{t("reporting.fetchError")}</Alert>}
      </Box>
      <Box
        sx={{
          // width: "1000px",
          height: 350,
        }}
      >
        {ds && ds?.data?.length > 0 && !isLoading ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={ds.data} margin={{ bottom: 20, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" padding={{ right: 20 }} />
              <YAxis
                domain={[0, getMax(ds.data)]}
                padding={{ top: 20 }}
                label={{
                  value: t("reporting.reservationClientsBehavior.yAxisLabel"),
                  angle: -90,
                  position: "insideLeft",
                  style: { textAnchor: "middle" },
                }}
              />
              <Tooltip />
              <Legend />

              <Line
                key={"new_clients"}
                type="monotone"
                dataKey={`new_clients`}
                stroke={DefaultColorMap[1]}
                activeDot={{ r: 8 }}
                name={`${t("reporting.reservationNewClients.count")}`}
              />
              <Line
                key={"returning_clients"}
                type="monotone"
                dataKey={`returning_clients`}
                stroke={DefaultColorMap[2]}
                activeDot={{ r: 8 }}
                name={`${t("reporting.reservationReturningClients.count")}`}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Box
            sx={{
              //grey box
              width: "100%",
              height: 300,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              bgcolor: "grey.100",
            }}
          >
            {isLoading ? (
              <CircularProgress />
            ) : (
              <Typography>{t("noData")}</Typography>
            )}
          </Box>
        )}
      </Box>
      <Snackbar
        open={!!snackMessage.message}
        autoHideDuration={6000}
        onClose={() => setSnackMessage({ message: "", severity: undefined })}
      >
        <Alert severity={snackMessage.severity}>{snackMessage.message}</Alert>
      </Snackbar>
    </Stack>
  )
}

export default OsReportReservationNewClients
